import clsx from 'clsx';
import { HeartIcon, LoaderCircleIcon, LucideProps, XIcon } from 'lucide-preact';
import { FunctionalComponent } from 'preact';
import { useMemo, useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';
import { buildURL } from 'Shared/fetch';
import { trackMP } from 'Shared/mp';
import useFetch from 'use-http';

import { routeToHabitat } from '@/components/HabitatLink/utils';
import { useSelector } from '@/redux/helper';
import { toggleFavoriteHabitat } from '@/redux/slices/user/actions';

interface FollowButtonBaseProps {
  habitatId: string;
  title: string;
  isInsideTheModal?: boolean;
}

interface FollowModeProps extends FollowButtonBaseProps {
  mode?: 'follow';
}

interface VisitModeProps extends FollowButtonBaseProps {
  mode: 'visit';
  habitatSlug: string;
  zooSlug: string;
}

type FollowButtonProps = FollowModeProps | VisitModeProps;

const FilledHeartIcon = ({ className, ...props }: LucideProps) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <HeartIcon className={clsx('fill-current text-system-pink', className)} {...props} />
);

const FollowButton: FunctionalComponent<FollowButtonProps> = ({
  habitatId,
  title,
  isInsideTheModal,
  mode = 'follow',
  ...rest
}) => {
  const { habitatSlug, zooSlug } =
    mode === 'visit' ? (rest as VisitModeProps) : { habitatSlug: undefined, zooSlug: undefined };
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.user.logged);
  const isFavorited = useSelector((state) => state.user?.favoriteHabitats?.includes(habitatId));
  const [error, setError] = useState<boolean>(false);

  const { post, del, response, loading } = useFetch(buildURL('/habitats/favorite'), {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });

  const favIcon = useMemo(() => {
    if (loading) {
      return LoaderCircleIcon;
    }
    if (error) {
      return XIcon;
    }
    if (isFavorited) {
      return FilledHeartIcon;
    }
    return HeartIcon;
  }, [isFavorited, loading, error]);

  const onFollow = async (evt: any) => {
    evt.stopPropagation();
    const trackingData = {
      habitatName: title,
      habitatId,
      place: 'inside-video-player',
    };

    if (isFavorited) {
      await del({ habitatId });
      trackMP('habitat-unfavourited', trackingData);
    } else {
      await post({ habitatId });
      trackMP('habitat-favourited', trackingData);
    }

    if (response.ok) {
      dispatch(toggleFavoriteHabitat(habitatId));
    } else {
      setError(true);
      setTimeout(() => {
        setError(false);
      }, 2000);
    }
  };

  const onVisit = (evt: MouseEvent) => {
    evt.stopPropagation();
    trackMP('visit-habitat-from-video-player', { habitatId, place: 'inside-video-player' });
    routeToHabitat({ id: habitatId, slug: habitatSlug!, zooSlug: zooSlug! }, { entryPoint: 'video-player' });
  };

  const buttonStyles = useMemo(() => {
    let baseSize = '';

    if (mode === 'visit') {
      baseSize = isInsideTheModal
        ? 'w-[76px] h-[28px] md:w-[144px] md:h-[42px] md:text-[14px] text-[12px]'
        : 'w-[76px] h-[28px] md:w-[112px] md:h-[44px] text-[12px] md:text-[18px]';
    } else if (isInsideTheModal && isFavorited) {
      baseSize = 'w-[102px] h-[31px] md:w-[144px] md:h-[42px] md:text-[14px] text-[12px]'; // following state in modal
    } else if (isInsideTheModal && !isFavorited) {
      baseSize = 'w-[80px] h-[31px] md:w-[108px] md:h-[42px] md:text-[14px] text-[12px]'; // default state in modal
    } else if (!isInsideTheModal && isFavorited) {
      baseSize = 'w-[102px] h-[31px] md:w-[160px] md:h-[44px] text-[12px] md:text-[18px]'; // following state regular
    } else {
      baseSize = 'w-[80px] h-[31px] md:w-[138px] md:h-[44px] text-[12px] md:text-[18px]'; // default state regular
    }

    const colorStyles =
      mode === 'visit'
        ? 'bg-white border-none'
        : !isFavorited
        ? 'bg-primary-yellow border-none'
        : 'bg-beige-light border-none';

    return `cursor-pointer rounded-full disabled:cursor-not-allowed ${baseSize} ${colorStyles}`;
  }, [isInsideTheModal, isFavorited, mode]);

  if (mode === 'follow' && !isLoggedIn) {
    return null;
  }

  if (mode === 'visit') {
    if (!habitatSlug || !zooSlug) {
      return null;
    }
    return (
      <div className={`${isInsideTheModal ? 'md:w-[144px]' : 'md:w-[112px]'} flex justify-center`}>
        <button type="button" onClick={onVisit} className={buttonStyles}>
          <span className="mx-2 font-medium text-primary-green">Visit</span>
        </button>
      </div>
    );
  }

  return (
    <div className={`${isInsideTheModal ? 'md:w-[144px]' : 'md:w-[130px]'} flex justify-center`}>
      <button type="button" disabled={loading} onClick={onFollow} className={buttonStyles}>
        <div className="flex items-center justify-center font-medium">
          {(() => {
            const FavIcon = favIcon;
            return (
              <FavIcon
                className={`ml-0.5 ${isInsideTheModal ? 'size-[12px] md:size-[18px]' : 'size-[14px] md:size-[20px]'} ${
                  favIcon === LoaderCircleIcon ? 'animate-spin' : ''
                }`}
                color="black"
              />
            );
          })()}
          <span className="mx-2 text-black">{isFavorited ? 'Following' : 'Follow'}</span>
        </div>
      </button>
    </div>
  );
};
export default FollowButton;
